<template>
  <Content
    class="shipment-list"
    aside-stretched
    full-width
    remove-card-wrapper
    remove-aside-card-padding
  >
    <template #header>
      <div
        class="content-header flex justify-content-between align-items-center"
      >
        <div
          class="content-heading-container flex flex-gap-1 align-items-center"
        >
          <SavedSearchControls
            v-model:selected-search="selectedSearch"
            :edited="isEdited"
            :popular-searches="popularSearches"
            :saved-searches="savedSearches"
            :header-text="t('shipments.shipments', 2)"
            header-icon="map-marked-alt"
            @clear="onSavedSearchClear"
            @delete="
              requestDeleteConfirmation({
                param: t('search.savedSearches'),
                accept: () => onSavedSearchDelete($event.search),
                target: $event.event.currentTarget as HTMLElement,
              })
            "
            @discard="onSavedSearchDiscard"
            @remove-as-default="onRemoveSearchAsDefault"
            @save-as-default="onSaveSearchAsDefault"
            @save="saveSearch"
            @select="onSavedSearchSelect"
            @update="onSaveSearchUpdate"
          />
          <LoadingIndicator
            :loading="results.loading || isBatchActionLoading"
          />
        </div>

        <span class="flex flex-gap-1 align-items-center">
          <InputWrapper
            v-if="isElasticsearchEnabledFeatureFlag"
            v-slot="{ inputClasses, inputId }"
            input-id="use-new-search-switch"
            :label="t('search.useNewSearch')"
            :label-help-text="t('search.useNewSearchTooltip')"
            label-icon="pi pi-info-circle"
            class="flash-info-light"
          >
            <RygenSwitch
              :id="inputId"
              v-model="isElasticsearchEnabledUserPreference"
              :class="inputClasses"
              @change="loadColumnsAndSearch(columnsLSKey)"
            />
          </InputWrapper>

          <ShipmentExportSearchResult
            :criteria="criteria"
            :visible-columns="columns"
          />
          <ShipmentBatchActionButton
            :selected-data="selectedRows"
            :loading="isBatchActionLoading"
            data-testid="shipment-list-batch-action-button"
            @command="onBatchActionCommand"
          />
          <ActionButton
            v-if="parcelActions.length"
            mode="secondary"
            :actions="parcelActions"
            data-testid="shipment-list-parcel-action-button"
          />
          <RygenButton
            v-if="$hasAuthority('shipment', 'manualEntry')"
            as="router-link"
            :to="{ name: 'shipmentEntry' }"
            :label="t('common.newParam', { param: t('shipments.shipments') })"
            icon="pi pi-plus"
            icon-pos="right"
            button-class="p-button-sm"
            data-testid="shipment-list-new-button"
          />
        </span>
      </div>
    </template>

    <SearchResultTableAndFilterWrapper
      :class="{ 'new-search-toggle': isElasticsearchEnabledFeatureFlag }"
    >
      <template #filters>
        <ShipmentSearchBuilder
          id="shipment-search-builder"
          :model-value="criteria"
          @update:model-value="onCriteriaChange"
        />
      </template>

      <template #default>
        <NewShipmentSearchResultView
          v-if="isElasticsearchEnabled"
          v-model:columns="columns"
          v-model:selection="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            selectedRows as EsShipment[]
          "
          :shipment-search-criteria="criteria"
          :results="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            results as Loadable<SearchResult<EsShipment>>
          "
          :handling-command="isBatchActionLoading"
          :column-options="availableColumns"
          @page="onPage"
          @sort="onSort"
          @command="onBatchActionCommand"
          @edited-columns="onEditedColumns"
          @refresh-no-cache="onRefreshNoCache"
        />
        <ShipmentSearchResultView
          v-else
          v-model:columns="columns"
          v-model:selection="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            selectedRows as ShipmentSearchData[]
          "
          :shipment-search-criteria="criteria"
          :results="
            // VSCode's syntax higlighting gets borked with single line casts in templates
            results as Loadable<SearchResult<ShipmentSearchData>>
          "
          :handling-command="isBatchActionLoading"
          :column-options="availableColumns"
          @page="onPage"
          @sort="onSort"
          @command="onBatchActionCommand"
          @edited-columns="onEditedColumns"
          @refresh-no-cache="onRefreshNoCache"
        />
      </template>
    </SearchResultTableAndFilterWrapper>

    <EditTagsModal
      v-model:visible="showEditTagsModal"
      :tag-options="tagOptions"
      @save="onBatchAddTagsConfirm"
    />

    <ShipmentBatchEventModal
      v-model="batchEvent"
      v-model:visible="isShowingBatchEventModal"
      @save="onBatchCreateEventConfirm"
    />

    <PrintShippingLabelTemplateModal
      v-model:visible="isShowingPrintShippingLabelTemplateModal"
      :show-warning="showShippingLabelTemplateRuleWarning"
      @confirm="onBatchPrintShippingLabelsConfirm"
    />

    <BatchCheckCallReminderErrorsModal
      v-model:visible="checkCallReminderErrorModal.visible"
      :errors="checkCallReminderErrorModal.errors"
    />

    <InviteCarriersToSpotMarketModal
      v-model:visible="inviteCarriersToSpotMarketModal.visible"
      :invite-command="inviteCarriersToSpotMarketModal.inviteCommand"
      :can-view-uplift="canViewUplift"
      @submit="onInviteCarriersToSpotMarketConfirm"
    />

    <SpotMarketErrorModal
      v-model:visible="spotMarketErrorModal.visible"
      :spot-markets="spotMarketErrorModal.erroredSpotMarkets"
    />

    <ParcelSchedulingModal
      v-model:visible="parcelSchedulingModal.visible"
      @success="onParcelSchedulingSuccess"
    />

    <ManifestModal
      v-model:visible="manifestModal.visible"
      @success="onManifestSuccess"
    />

    <ManifestSuccessModal
      v-model:visible="manifestSuccessModal.visible"
      :result="manifestSuccessModal.results"
    />

    <ParcelSchedulingSuccessModal
      v-model:visible="parcelSchedulingSuccessModal.visible"
      :result="parcelSchedulingSuccessModal.results"
    />

    <BulkTenderModal
      v-model:visible="isShowingBulkTenderModal"
      :selected-shipments="selectedRows"
      @shipments-tendered="onBatchTenderConfirm"
    />

    <BulkTenderResultModal
      v-model:visible="bulkTenderResultModal.visible"
      :results="bulkTenderResultModal.results"
    />

    <AssignUsersModal
      v-model:visible="assignUsersModal.visible"
      :assignable-users="assignUsersModal.assignableUsers"
      @save="onBatchAssignUsersConfirm"
    />

    <BatchUpdateShipmentHealthModal
      v-model:visible="showUpdateHealthModal"
      @save="onBatchUpdateHealthConfirm"
    />
  </Content>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { CancelTokenSource, default as axios } from 'axios';
import { useConfirm } from 'primevue/useconfirm';
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  ToastMessage,
  useDeleteConfirmation,
  usePartitionedStorageKey,
  useQueryParamsForSearch,
  useToast,
} from '@rygen/rygen-ui/composables';
import {
  ActionCommand,
  ClientReferenceNumberType,
  ColumnSpecification,
  DropdownOption,
  Loadable,
  ReferenceNumberTypeLevel,
  RygenMenuItem,
  SearchResult,
  TenderMethod,
  UserRef,
} from '@rygen/rygen-ui/model';
import { distinct, distinctBy, notNull } from '@rygen/rygen-ui/util';
import { ReferenceNumber as EsReferenceNumber } from '@rygen/tms-search-model/search/referenceNumber';
import { Shipment as EsShipment } from '@rygen/tms-search-model/search/shipment';

import api from '@/api';
import {
  useElasticsearch,
  useLocationGrouping,
  useReferenceNumberTypes,
  useSavedSearches,
} from '@/composables';
import {
  BulkTenderResult,
  ColumnProps,
  DocumentType,
  ES_SHIPMENT_COLUMNS,
  EsShipmentColumnProps,
  InviteCommand,
  isShipmentColumnProps,
  ManifestResponse,
  ParcelSchedulingResponse,
  ReferenceNumber,
  Shipment,
  SHIPMENT_COLUMNS,
  ShipmentAction,
  ShipmentBatchEvent,
  ShipmentEventType,
  ShipmentHealth,
  ShipmentInviteCarriersToSpotMarketBatchCommand,
  ShipmentSavedSearch,
  ShipmentSearchCriteria,
  ShipmentSearchData,
  ShipmentSearchResult,
  ShipmentStatus,
  ShippingLabelFormat,
  SpotMarket,
  TenderAcceptRequest,
  TenderRejectRequest,
  UserAssignmentType,
} from '@/model';
import { useAssignableUserStore } from '@/stores/AssignableUser';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useLocationGroupStore } from '@/stores/LocationGroup';
import { useSpotMarketStore } from '@/stores/SpotMarket';
import { useTagStore } from '@/stores/Tag';
import { formatReferenceNumberValue } from '@/util';
import {
  deliverShippingLabel,
  shippingLabelErrorDetail,
} from '@/util/ShippingLabelUtil';

import InviteCarriersToSpotMarketModal from '@/components/carrierPortal/InviteCarriersToSpotMarketModal.vue';
import SpotMarketErrorModal from '@/components/carrierPortal/SpotMarketErrorModal.vue';
import BatchCheckCallReminderErrorsModal from '@/components/shipment/BatchCheckCallReminderErrorsModal.vue';
import BatchUpdateShipmentHealthModal from '@/components/shipment/BatchUpdateShipmentHealthModal.vue';
import BulkTenderModal from '@/components/shipment/BulkTenderModal.vue';
import BulkTenderResultModal from '@/components/shipment/BulkTenderResultModal.vue';
import ManifestModal from '@/components/shipment/manifest/ManifestModal.vue';
import ManifestSuccessModal from '@/components/shipment/manifest/ManifestSuccessModal.vue';
import NewShipmentSearchResultView from '@/components/shipment/NewShipmentSearchResult.vue';
import ParcelSchedulingModal from '@/components/shipment/parcel/ParcelSchedulingModal.vue';
import ParcelSchedulingSuccessModal from '@/components/shipment/parcel/ParcelSchedulingSuccessModal.vue';
import PrintShippingLabelTemplateModal from '@/components/shipment/PrintShippingLabelTemplateModal.vue';
import ShipmentBatchActionButton from '@/components/shipment/ShipmentBatchActionButton.vue';
import ShipmentBatchEventModal from '@/components/shipment/ShipmentBatchEventModal.vue';
import ShipmentExportSearchResult from '@/components/shipment/ShipmentExportSearchResult.vue';
import ShipmentSearchBuilder from '@/components/shipment/ShipmentSearchBuilder.vue';
import ShipmentSearchResultView from '@/components/shipment/ShipmentSearchResult.vue';
import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenSwitch from '@rygen/rygen-ui/components/form/RygenSwitch.vue';
import SavedSearchControls from '@rygen/rygen-ui/components/search/SavedSearchControls.vue';
import EditTagsModal from '@rygen/rygen-ui/components/tag/EditTagsModal.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import SearchResultTableAndFilterWrapper from '@rygen/rygen-ui/components/ui/SearchResultTableAndFilterWrapper.vue';
import AssignUsersModal from '@rygen/rygen-ui/components/user/AssignUsersModal.vue';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

/**
 * The number in milliseconds to wait after a batch action before refreshing
 * search results
 **/
const BATCH_ACTION_DELAY = 5000;

const parcelTenderingEnabled = useFlag('parcelTendering');
const parcelManifestingEnabled = useFlag('parcel-manifest-feat-flag');

const {
  isElasticsearchEnabled,
  isElasticsearchEnabledFeatureFlag,
  isElasticsearchEnabledUserPreference,
} = useElasticsearch({ searchType: 'shipment' });
const { filterLocationGroupColumns, isLocationGroupingEnabled } =
  useLocationGrouping();
const { loadLocationGroups } = useLocationGroupStore();
const { loadAssignableUsersByDomainId } = useAssignableUserStore();
const confirm = useConfirm();
const { currentUser, selectedDomain } = useCurrentUserStore();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { t } = useI18n();
const { storageKey: columnsLSKey } = usePartitionedStorageKey(
  'shipmentColumns',
  {
    currentUser,
    selectedDomain,
  },
);

const runSearch = async (
  criteria: ShipmentSearchCriteria,
  cancelRequest?: CancelTokenSource,
) => {
  try {
    results.value.loading = true;
    results.value.data = isElasticsearchEnabled.value
      ? await api.shipment.searchWithElasticSearch(criteria, {
          cancelToken: cancelRequest,
          useCache: useCache.value,
        })
      : await api.shipment.search(criteria, {
          cancelToken: cancelRequest,
          useCache: useCache.value,
        });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    results.value.error = e;
    console.error('Error loading shipments', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('shipments.shipments', 2),
      }),
      error: e,
    });
  } finally {
    results.value.loading = false;
    useCache.value = true;
  }
};

const { criteria, runSearchDebounced, searchQueryString } =
  useQueryParamsForSearch({
    factoryMethod: (opts?: Partial<ShipmentSearchCriteria>) =>
      new ShipmentSearchCriteria(opts),
    routeName: 'shipmentList',
    skipSearchOnMount: true,
    runSearch,
  });

const {
  lineItemReferenceNumberTypes,
  loadReferenceNumberTypes,
  orderReferenceNumberTypes,
  shipmentReferenceNumberTypes,
} = useReferenceNumberTypes();
const {
  createSavedSearch,
  deleteSavedSearch,
  loadPopularSearches,
  loadSavedSearches,
  popularSearches,
  savedSearches,
  updateSavedSearch,
} = useSavedSearches('Shipment');
const { inviteCarriersToSpotMarket } = useSpotMarketStore();
const { loadTags, tagOptions } = useTagStore();
const toast = useToast();

const columns: Ref<ColumnSpecification[]> = ref(
  filterLocationGroupColumns(
    isElasticsearchEnabled.value ? ES_SHIPMENT_COLUMNS : SHIPMENT_COLUMNS,
  ),
);
const selectedRows: Ref<ShipmentSearchData[] | EsShipment[]> = ref([]);
const results: Ref<Loadable<SearchResult<ShipmentSearchData | EsShipment>>> =
  ref({
    loading: false,
    data: new ShipmentSearchResult(),
  });
const isShowingBatchEventModal = ref(false);
const isShowingPrintShippingLabelTemplateModal = ref(false);
const showShippingLabelTemplateRuleWarning = ref(false);
const batchEvent: Ref<ShipmentBatchEvent | undefined> = ref(undefined);
const selectedSearch: Ref<ShipmentSavedSearch | undefined> = ref();
const isEdited = ref(false);
const isBatchActionLoading = ref(false);
const showEditTagsModal = ref(false);
const showUpdateHealthModal = ref(false);
const isShowingBulkTenderModal = ref(false);
const unscheduledParcelShipmentsExist = ref(false);
const useCache = ref(true);
const canViewUplift = ref(false);
const checkCallReminderErrorModal = ref({
  visible: false,
  errors: [] as string[],
});
const bulkTenderResultModal = ref({
  visible: false,
  results: [] as BulkTenderResult[],
});
const inviteCarriersToSpotMarketModal = ref({
  visible: false,
  inviteCommand: new ShipmentInviteCarriersToSpotMarketBatchCommand(),
});
const spotMarketErrorModal = ref({
  visible: false,
  erroredSpotMarkets: [] as SpotMarket[],
});
const parcelSchedulingModal = ref({
  visible: false,
});
const parcelSchedulingSuccessModal = ref({
  visible: false,
  results: new ParcelSchedulingResponse(),
});
const manifestModal = ref({
  visible: false,
});
const manifestSuccessModal = ref({
  visible: false,
  results: new ManifestResponse(),
});
const assignUsersModal = ref({
  visible: false,
  assignableUsers: { loading: false } as Loadable<UserRef[]>,
});

const referenceNumbers: Ref<ClientReferenceNumberType[]> = computed(() => {
  const shipmentTypes = shipmentReferenceNumberTypes.value.data ?? [];
  const orderTypes = orderReferenceNumberTypes.value.data ?? [];
  const lineItemTypes = lineItemReferenceNumberTypes.value.data ?? [];

  return [...shipmentTypes, ...orderTypes, ...lineItemTypes]
    .filter(distinct())
    .sort((a, b) => {
      const aName = a.referenceNumberType?.name ?? '';
      const bName = b.referenceNumberType?.name ?? '';
      return aName < bName ? -1 : aName > bName ? 1 : 0;
    });
});

const referenceNumberValueOptionsMap = computed(() => {
  const entries = referenceNumbers.value
    .map<
      | [naturalId: string, valueOptionMap: Map<any, DropdownOption<any>>]
      | undefined
    >((clientRefNum) => {
      const refNumType = clientRefNum.referenceNumberType;
      if (!refNumType?.valueOptions || !refNumType.naturalId) {
        return undefined;
      }

      const valueOptionsEntries: [any, DropdownOption<any>][] =
        refNumType.valueOptions.map((opt) => [opt.value, opt]) ?? [];
      return [refNumType.naturalId, new Map(valueOptionsEntries)];
    })
    .filter(notNull);

  return new Map(entries);
});

const referenceNumberColumns = computed(() => {
  return (
    referenceNumbers.value.map((clientRefNum) => {
      const refNumNaturalId = clientRefNum.referenceNumberType?.naturalId!;
      const isLineItemType = clientRefNum.levels?.includes(
        ReferenceNumberTypeLevel.LINE_ITEM,
      );
      const isOrderType = clientRefNum.levels?.includes(
        ReferenceNumberTypeLevel.ORDER,
      );

      return new ColumnSpecification({
        key: refNumNaturalId,
        field: refNumNaturalId,
        header: clientRefNum.referenceNumberType?.name,
        sortable: true,
        body: (data: ColumnProps) => {
          // Build up an array of all possible ref num matches
          let refNums: ReferenceNumber[] = data.data?.referenceNumbers ?? [];
          if (isLineItemType) {
            refNums = refNums.concat(data.data?.lineItemReferenceNumbers ?? []);
          }
          if (isShipmentColumnProps(data) && isOrderType) {
            refNums = refNums.concat(data.data?.relatedReferenceNumbers ?? []);
          }

          // Find any matches in the array and map them to a single string
          return refNums
            .filter((refNum) => refNum.typeNaturalId === refNumNaturalId)
            .map((refNum) =>
              formatReferenceNumberValue(
                referenceNumberValueOptionsMap,
                clientRefNum,
                refNum,
              ),
            )
            .join(', ');
        },
      });
    }) ?? []
  );
});

const esReferenceNumberColumns = computed(() => {
  return referenceNumbers.value.map((clientRefNum) => {
    const refNumNaturalId = clientRefNum.referenceNumberType?.naturalId!;

    return new ColumnSpecification({
      key: `referenceNumbers.${refNumNaturalId}.values.keyword`,
      field: `referenceNumbers.${refNumNaturalId}.values.keyword`,
      header: clientRefNum.referenceNumberType?.name,
      sortable: true,
      body: ({ data: shipment }: EsShipmentColumnProps) => {
        // Build up an array of all possible ref num matches
        const refNums: Array<EsReferenceNumber | undefined> = [];

        refNums.push(shipment.referenceNumbers?.[refNumNaturalId]);
        refNums.push(shipment.relatedReferenceNumbers?.[refNumNaturalId]);
        shipment.lineItems?.forEach((l) =>
          refNums.push(l.referenceNumbers?.[refNumNaturalId]),
        );

        // Find any matches in the array and map them to a single string
        return refNums
          .flatMap((refNum) => refNum?.values)
          .filter((x) => !!x?.trim())
          .join(', ');
      },
    });
  });
});

const resolvedShipmentColumns: Ref<ColumnSpecification[]> = computed(() => {
  return filterLocationGroupColumns(
    isElasticsearchEnabled.value ? ES_SHIPMENT_COLUMNS : SHIPMENT_COLUMNS,
  );
});

const resolvedRefNumColumns: Ref<ColumnSpecification[]> = computed(() =>
  isElasticsearchEnabled.value
    ? esReferenceNumberColumns.value
    : referenceNumberColumns.value,
);

const availableColumns: Ref<ColumnSpecification[]> = computed(() => {
  return [
    ...resolvedShipmentColumns.value,
    ...resolvedRefNumColumns.value,
  ].filter(distinctBy('key'));
});

const availableColumnsByKey = computed(
  () => new Map(availableColumns.value.map((col) => [col.key, col])),
);

const availableColumnsByAlias = computed(() => {
  const aliasMap = new Map<string, ColumnSpecification>();
  for (const col of availableColumns.value) {
    for (const alias of col.alias ?? []) {
      aliasMap.set(alias, col);
    }
  }
  return aliasMap;
});

const onCriteriaChange = (newVal: ShipmentSearchCriteria) => {
  criteria.value = new ShipmentSearchCriteria(newVal);
  criteria.value.page = 0;
  isEdited.value = true;
};

const onPage = (event: DataTablePageEvent) => {
  criteria.value.pageSize = event.rows as number;
  criteria.value.page = event.page as number;
};

const onSort = (event: DataTableSortEvent) => {
  criteria.value.sortField = event.sortField as keyof Shipment | undefined;
  criteria.value.sortOrder = (event.sortOrder ?? 0) as -1 | 0 | 1;
};

const onParcelSchedulingSuccess = (event: ParcelSchedulingResponse) => {
  parcelSchedulingSuccessModal.value.results = new ParcelSchedulingResponse({
    ...event,
  });
  parcelSchedulingSuccessModal.value.visible = true;
};

const onBatchActionCommand = ({ action }: ActionCommand<ShipmentAction>) => {
  if (selectedRows.value === undefined) {
    toast.addWarning({ detail: t('batchAction.noSelection') });
    return;
  }

  if (action === ShipmentAction.ADD_TAGS) {
    onBatchAddTags();
  } else if (action === ShipmentAction.CANCEL_CHECK_CALL_EMAILS) {
    onBatchCancelCheckCallReminders();
  } else if (action === ShipmentAction.ASSIGN_USERS) {
    const ownerId = selectedRows.value[0].owner?.id ?? 0;
    onBatchAssignUsers(ownerId);
  } else if (action === ShipmentAction.CARRIER_UNASSIGN) {
    onBatchCarrierUnassign();
  } else if (action === ShipmentAction.EVENT_DELIVERED_CREATE) {
    onBatchCreateDeliveredEvent();
  } else if (action === ShipmentAction.EVENT_PICKUP_CREATE) {
    onBatchCreatePickupEvent();
  } else if (action === ShipmentAction.PRINT_BOL) {
    onBatchPrintBOL();
  } else if (action === ShipmentAction.PRINT_SHIPPING_LABEL_MODAL) {
    onBatchPrintShippingLabels();
  } else if (action === ShipmentAction.REQUEST_SPOT_MARKET_BID) {
    onInviteCarriersToSpotMarket();
  } else if (action === ShipmentAction.SEND_CHECK_CALL_REQUEST) {
    onBatchSendCheckCallReminders();
  } else if (action === ShipmentAction.TENDER_TO_CARRIER) {
    onBatchTender();
  } else if (action === ShipmentAction.TENDER_CONFIRM) {
    onBatchTenderAccept();
  } else if (action === ShipmentAction.TENDER_REJECT) {
    onBatchTenderReject();
  } else if (action === ShipmentAction.UPDATE_HEALTH) {
    onBatchUpdateHealth();
  } else {
    toast.addWarning({
      detail: t('batchAction.paramNotSupported', {
        param: action,
      }),
    });
    selectedRows.value = [];
  }
};

const onBatchActionSuccess = async (toastMessage?: ToastMessage) => {
  results.value.loading = true;
  selectedRows.value = [];

  if (isElasticsearchEnabled.value) {
    await new Promise((resolve) => setTimeout(resolve, BATCH_ACTION_DELAY));
  }

  if (toastMessage) {
    toast.addSuccess(toastMessage);
  }

  runSearchDebounced();
};

const onBatchAddTags = () => {
  loadTags();
  showEditTagsModal.value = true;
};

const onBatchAddTagsConfirm = async (newTags: string[]) => {
  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.shipmentBatch.addTags(shipmentIds, newTags);

    loadTags({ bypassCache: true });
    onBatchActionSuccess({
      detail: t('messages.successAssigningParam', {
        param: t('tags.tags', newTags.length),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch adding tags', e);
    toast.addError({
      detail: t('errors.assigningParam', {
        param: t('tags.tags', newTags.length),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchCancelCheckCallReminders = async () => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    const response =
      await api.shipmentBatch.batchCancelCheckCallReminders(shipmentIds);

    const numSuccesses = numShipments - response.errors.length;
    if (numSuccesses) {
      onBatchActionSuccess({
        detail: t('messages.successCancelingParam', {
          param: t(
            'carrierPortal.checkCallReminders.checkCallReminders',
            numSuccesses,
          ),
        }),
      });
    }

    if (response.errors.length) {
      checkCallReminderErrorModal.value.errors = response.errors;
      checkCallReminderErrorModal.value.visible = true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch canceling check call reminders', e);
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t(
          'carrierPortal.checkCallReminders.checkCallReminders',
          numShipments,
        ),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchAssignUsers = async (domainId: number) => {
  assignUsersModal.value.assignableUsers = loadAssignableUsersByDomainId(
    domainId,
    'shipment',
  );
  assignUsersModal.value.visible = true;
};

const onBatchAssignUsersConfirm = async (
  selectedUsers: UserRef[],
  type: UserAssignmentType,
) => {
  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.shipmentBatch.assignUsers(shipmentIds, selectedUsers, type);

    onBatchActionSuccess({
      detail: t('messages.successAssigningParam', {
        param: t('users.users', selectedUsers.length),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.assigningParam', {
        param: t('common.users', selectedUsers.length),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchUpdateHealth = () => {
  showUpdateHealthModal.value = true;
};

const onBatchUpdateHealthConfirm = async (health: ShipmentHealth) => {
  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.shipmentBatch.updateHealth(shipmentIds, health);

    onBatchActionSuccess({
      detail: t('messages.successUpdatingParam', {
        param: t('shipments.health'),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch updating shipment health', e);
    toast.addError({
      detail: t('errors.updatingParam', { param: t('shipments.health') }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchCarrierUnassign = async () => {
  if (selectedRows.value[0].status === ShipmentStatus.BOOKED) {
    confirm.require({
      group: 'rygen-confirm-dialog',
      header: t('common.unassignParam', {
        param: t('carriers.carriers', selectedRows.value.length),
      }),
      message: t('shipments.removeCarriersMessage', selectedRows.value.length),
      accept: onBatchCarrierUnassignConfirm,
      acceptClass: 'p-button-sm',
      acceptLabel: t('common.unassignParam', {
        param: t('carriers.carriers', selectedRows.value.length),
      }),
      rejectClass: 'p-button-sm p-button-text',
      rejectLabel: t('common.cancel'),
    });
  } else {
    onBatchCarrierUnassignConfirm();
  }
};

const onBatchCarrierUnassignConfirm = async () => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.shipmentBatch.batchCarrierUnassign(shipmentIds);

    onBatchActionSuccess({
      detail: t('messages.successUnassigningParam', {
        param: t('carriers.carriers', numShipments),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch unassigning carriers', e);
    toast.addError({
      detail: t('errors.unassigningParam', {
        param: t('carriers.carriers', numShipments),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchCreateDeliveredEvent = () => {
  batchEvent.value = new ShipmentBatchEvent();
  batchEvent.value.eventType = ShipmentEventType.DROPOFF;
  isShowingBatchEventModal.value = true;
};

const onBatchCreatePickupEvent = () => {
  batchEvent.value = new ShipmentBatchEvent();
  batchEvent.value.eventType = ShipmentEventType.CARRIER_PICKED_UP;
  isShowingBatchEventModal.value = true;
};

const onBatchCreateEventConfirm = async () => {
  if (!batchEvent.value) {
    return;
  }

  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    await api.shipmentBatchEvent.createBatch(shipmentIds, batchEvent.value);

    onBatchActionSuccess({
      detail: t('messages.successCreatingParam', {
        param: t('events.events', numShipments),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch creating events', e);
    toast.addError({
      detail: t('errors.creatingParam', {
        param: t('events.events', numShipments),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchPrintBOL = async () => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    const blob = await api.shipmentDocumentBatch.generateBatchBol(shipmentIds);

    const fileName = 'bols.pdf';
    const file = new File([blob], fileName, { type: 'application/pdf' });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_new';
    link.download = fileName;
    link.click();
    link.remove();

    onBatchActionSuccess();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error downloading BOLs', e);
    toast.addError({
      detail: t('shipments.errors.bolDownload', numShipments),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchPrintShippingLabels = async () => {
  // Check if current domain has templates available and show a warning if it
  // does that they will override the default selection in the modal
  showShippingLabelTemplateRuleWarning.value = false;
  const shipmentId = selectedRows.value.at(0)?.id;
  if (shipmentId != null) {
    try {
      const { templates } = await api.documentTemplate.matchDocumentTemplate(
        DocumentType.SHIPPING_LABEL,
        shipmentId,
      );
      showShippingLabelTemplateRuleWarning.value = !!templates.length;
    } catch (e) {
      console.error('load shipping label templates error', shipmentId, e);
    }
  }

  isShowingPrintShippingLabelTemplateModal.value = true;
};

const onBatchPrintShippingLabelsConfirm = async (
  type: string,
  _templateId: number | undefined,
  format: ShippingLabelFormat,
) => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    const blob = await api.shipmentDocumentBatch.generateBatchShippingLabels(
      shipmentIds,
      type,
      format,
    );
    deliverShippingLabel(blob, 'shippingLabels', format, {
      openInNewTab: false,
    });

    onBatchActionSuccess();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error downloading shipping labels', e);
    toast.addError({
      detail:
        shippingLabelErrorDetail(e) ??
        t('shipments.errors.shippingLabelDownload', numShipments),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const getCanViewUpliftPermission = async () => {
  try {
    canViewUplift.value = await api.user.getHasUpliftPermission();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching uplift permission', e);
    canViewUplift.value = false;
  }
};

const onInviteCarriersToSpotMarket = () => {
  getCanViewUpliftPermission();
  inviteCarriersToSpotMarketModal.value.visible = true;
  inviteCarriersToSpotMarketModal.value.inviteCommand.shipmentIds =
    selectedRows.value.map((row) => row.id).filter(notNull);
};

const onInviteCarriersToSpotMarketConfirm = async (command: InviteCommand) => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const { erroredSpotMarkets } = await inviteCarriersToSpotMarket(command);
    inviteCarriersToSpotMarketModal.value.visible = false;

    const numExpectedSpotMarkets = numShipments * command.scacs.length;
    const numSuccesses = numExpectedSpotMarkets - erroredSpotMarkets.length;
    if (numSuccesses) {
      onBatchActionSuccess();
    }

    if (erroredSpotMarkets.length) {
      spotMarketErrorModal.value.erroredSpotMarkets = erroredSpotMarkets;
      spotMarketErrorModal.value.visible = true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    // nothing to do here
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchSendCheckCallReminders = async () => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    const response =
      await api.shipmentBatch.batchSendCheckCallReminders(shipmentIds);

    const numSuccesses = numShipments - response.errors.length;
    if (numSuccesses) {
      onBatchActionSuccess({
        detail: t('messages.successSendingParam', {
          param: t(
            'carrierPortal.checkCallReminders.checkCallReminders',
            numSuccesses,
          ),
        }),
      });
    }

    if (response.errors.length) {
      checkCallReminderErrorModal.value.errors = response.errors;
      checkCallReminderErrorModal.value.visible = true;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch sending check call reminders', e);
    toast.addError({
      detail: t('errors.sendingParam', {
        param: t(
          'carrierPortal.checkCallReminders.checkCallReminders',
          numShipments,
        ),
      }),
      error: e,
    });
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchTender = () => {
  isShowingBulkTenderModal.value = true;
};

const onBatchTenderConfirm = (results: BulkTenderResult[]) => {
  isBatchActionLoading.value = true;
  bulkTenderResultModal.value.results = results;
  bulkTenderResultModal.value.visible = true;
  onBatchActionSuccess({
    detail: t('messages.successSendingParam', {
      param: t('shipments.tender', results.length),
    }),
  });
  isBatchActionLoading.value = false;
};

const onBatchTenderAccept = async () => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    const tenderAcceptRequest = new TenderAcceptRequest(TenderMethod.MANUAL);
    await api.shipmentBatch.tenderAcceptBatch(shipmentIds, tenderAcceptRequest);

    onBatchActionSuccess({
      detail: t('messages.successConfirmingParam', {
        param: t('shipments.tender', numShipments),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch accepting tenders', e);
    toast.addError({
      detail: t('errors.confirmingParam', {
        param: t('shipments.tender', numShipments),
      }),
      error: e,
    });
    selectedRows.value = [];
    runSearchDebounced();
  } finally {
    isBatchActionLoading.value = false;
  }
};

const onBatchTenderReject = async () => {
  const numShipments = selectedRows.value.length;

  try {
    isBatchActionLoading.value = true;

    const shipmentIds = selectedRows.value.map((row) => row.id).filter(notNull);
    const tenderRejectRequest = new TenderRejectRequest(TenderMethod.MANUAL);
    await api.shipmentBatch.tenderRejectBatch(shipmentIds, tenderRejectRequest);

    onBatchActionSuccess({
      detail: t('messages.successRejectingParam', {
        param: t('shipments.tender', numShipments),
      }),
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error batch rejecting tenders', e);
    toast.addError({
      detail: t('errors.rejectingParam', {
        param: t('shipments.tender', numShipments),
      }),
      error: e,
    });
    selectedRows.value = [];
    runSearchDebounced();
  } finally {
    isBatchActionLoading.value = false;
  }
};

const saveSearch = async (savedSearchInfo: {
  name: string;
  defaultSearch: boolean;
}) => {
  const search = new ShipmentSavedSearch({
    name: savedSearchInfo.name,
    searchData: new ShipmentSearchCriteria(criteria.value),
    searchColumnKeys: columns.value.map((col) => col.key),
    defaultSearch: savedSearchInfo.defaultSearch,
  });
  const savedSearch = await createSavedSearch(search);
  if (savedSearch) {
    selectedSearch.value = savedSearch;
    isEdited.value = false;
  }
};

const onSaveSearchUpdate = async (savedSearch: ShipmentSavedSearch) => {
  const search = new ShipmentSavedSearch({
    ...savedSearch,
    searchData: new ShipmentSearchCriteria(criteria.value),
    searchColumnKeys: columns.value.map((col) => col.key),
  });
  const updatedSearch = await updateSavedSearch(search);
  if (updatedSearch) {
    selectedSearch.value = savedSearch;
    isEdited.value = false;
  }
};

const onSaveSearchAsDefault = async (savedSearch: ShipmentSavedSearch) => {
  const search = new ShipmentSavedSearch({
    ...savedSearch,
    defaultSearch: true,
  });

  const updatedSearch = await updateSavedSearch(search);
  if (updatedSearch) {
    selectedSearch.value = updatedSearch;
    isEdited.value = false;
  }
};

const onRemoveSearchAsDefault = async (savedSearch: ShipmentSavedSearch) => {
  const search = new ShipmentSavedSearch({
    ...savedSearch,
    defaultSearch: false,
  });
  const updatedSearch = await updateSavedSearch(search);
  if (updatedSearch) {
    selectedSearch.value = updatedSearch;
    isEdited.value = false;
  }
};

const onSavedSearchDelete = (savedSearch: ShipmentSavedSearch) => {
  if (!savedSearch.id) {
    return;
  }

  deleteSavedSearch(savedSearch.id);
  selectedSearch.value = undefined;
  criteria.value = new ShipmentSearchCriteria();
  isEdited.value = false;
};

const onSavedSearchClear = () => {
  criteria.value = new ShipmentSearchCriteria();
  columns.value = getColumnsFromKeys(columnsLSKey.value);
  isEdited.value = false;
};

const onSavedSearchSelect = (savedSearch: ShipmentSavedSearch) => {
  criteria.value = new ShipmentSearchCriteria({
    ...savedSearch.searchData,
    savedSearchId: savedSearch.id,
  });
  columns.value = getColumnsFromKeys(columnsLSKey.value, savedSearch);
  isEdited.value = false;
};

const onSavedSearchDiscard = (savedSearch?: ShipmentSavedSearch) => {
  criteria.value = new ShipmentSearchCriteria(savedSearch?.searchData);

  const showAllColumns = !savedSearch || savedSearch.isPopularSearch;
  columns.value = showAllColumns
    ? availableColumns.value
    : getColumnsFromKeys(columnsLSKey.value, savedSearch);

  isEdited.value = false;
};

const onShowParcelSchedulingModal = () => {
  parcelSchedulingModal.value.visible = true;
};

const onShowManifestModal = () => {
  manifestModal.value.visible = true;
};

const onManifestSuccess = (event: ManifestResponse) => {
  manifestSuccessModal.value.results = new ManifestResponse({ ...event });
  manifestSuccessModal.value.visible = true;
  void runSearch(criteria.value);
};

const parcelActions = computed<RygenMenuItem[]>(() =>
  [
    {
      label: t('parcelScheduling.scheduleParcelPickup'),
      icon: 'pi pi-calendar-plus',
      visible: unscheduledParcelShipmentsExist.value,
      command: () => onShowParcelSchedulingModal(),
    },
    {
      label: t('manifests.createManifest'),
      icon: 'pi pi-file',
      visible: parcelManifestingEnabled.value,
      command: () => onShowManifestModal(),
    },
  ].filter((action) => action.visible),
);

const getPopularSearchStorageKey = (
  savedSearch: ShipmentSavedSearch,
  lsKey: string,
) => {
  return `${lsKey}-${savedSearch.name?.replaceAll(' ', '_')}`;
};

const onEditedColumns = (newColumns: ColumnSpecification[]) => {
  // Not handled in watcher because internal state updates by itself
  isEdited.value = true;

  // Not handled in watcher because columns change before selected search
  const isAllSearch = !selectedSearch.value;
  if (isAllSearch) {
    localStorage.setItem(
      columnsLSKey.value,
      ColumnSpecification.serialize(newColumns),
    );
  } else if (selectedSearch.value?.isPopularSearch) {
    localStorage.setItem(
      getPopularSearchStorageKey(selectedSearch.value, columnsLSKey.value),
      ColumnSpecification.serialize(newColumns),
    );
  }
};

const onRefreshNoCache = () => {
  useCache.value = false;
  runSearchDebounced();
};

const getColumnFromJSON = (value: string) =>
  availableColumnsByKey.value.get(value) ??
  availableColumnsByAlias.value.get(value);

const getColumnsFromKeys = (
  lsKey: string,
  savedSearch?: ShipmentSavedSearch,
): ColumnSpecification[] => {
  // User is on user-made saved search with saved columns
  if (savedSearch?.searchColumnKeys.length) {
    return savedSearch.searchColumnKeys.map(getColumnFromJSON).filter(notNull);
  }

  let storageColumns: ColumnSpecification[] | undefined;
  if (savedSearch && savedSearch.isPopularSearch) {
    // User is on a default search that should have its own local storage values
    storageColumns = ColumnSpecification.deserialize(
      localStorage.getItem(getPopularSearchStorageKey(savedSearch, lsKey)),
      getColumnFromJSON,
    );
    // The default search hasn't been used before so load their "All" search columns
    if (storageColumns === undefined) {
      storageColumns = ColumnSpecification.deserialize(
        localStorage.getItem(lsKey),
        getColumnFromJSON,
      );
    }
  } else {
    // User is on "All" search or an outdated saved search with no saved columns
    storageColumns = ColumnSpecification.deserialize(
      localStorage.getItem(lsKey),
      getColumnFromJSON,
    );
  }

  if (storageColumns === undefined) {
    // User has no local storage so show all columns
    storageColumns = availableColumns.value;
  }

  return storageColumns;
};

const findSelectedSearch = (savedSearchId: number | undefined) => {
  if (!savedSearchId) {
    return;
  }

  const allSearches = [
    ...(popularSearches.value.data ?? []),
    ...(savedSearches.value.data ?? []),
  ];
  return allSearches.find(({ id }) => id === savedSearchId);
};

const loadAndSetSelectedSearch = async (lsKey: string) => {
  await Promise.all([loadPopularSearches(), loadSavedSearches()]);
  selectedSearch.value = findSelectedSearch(criteria.value.savedSearchId);
  columns.value = getColumnsFromKeys(lsKey, selectedSearch.value);
};

const loadColumnsAndSearch = async (lsKey: string) => {
  // Domain can be undefined on first load so wait until its correct
  if (!selectedDomain.value) {
    return;
  }

  await Promise.all([
    loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.SHIPMENT }),
    loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.ORDER }),
    loadReferenceNumberTypes({ level: ReferenceNumberTypeLevel.LINE_ITEM }),
  ]);

  if (searchQueryString.value) {
    // URL already has search params, fire search immediately
    void loadAndSetSelectedSearch(lsKey);
    void runSearch(criteria.value);
    return;
  }

  await Promise.all([loadPopularSearches(), loadSavedSearches()]);

  const defaultSearch = savedSearches.value.data?.find(
    (search) => search.defaultSearch,
  );
  if (defaultSearch) {
    selectedSearch.value = defaultSearch;
    criteria.value = new ShipmentSearchCriteria({
      ...defaultSearch.searchData,
      savedSearchId: defaultSearch.id,
    });
    columns.value = getColumnsFromKeys(lsKey, defaultSearch);
    isEdited.value = false;
    void runSearch(criteria.value);
    return;
  }

  selectedSearch.value = findSelectedSearch(criteria.value.savedSearchId);
  columns.value = getColumnsFromKeys(lsKey, selectedSearch.value);
  void runSearch(criteria.value);
};

const checkUnscheduledParcelShipmentsExist = async () => {
  if (!parcelTenderingEnabled.value) {
    return;
  }

  try {
    unscheduledParcelShipmentsExist.value = !!(
      await api.shipmentParcel.doesUnscheduledParcelShipmentExist()
    ).unscheduledShipmentsExist;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error loading unscheduled parcel shipments', e);
  }
};

watch(selectedSearch, (newVal: ShipmentSavedSearch | undefined) => {
  criteria.value.savedSearchId = newVal?.id;
});

watch(
  columnsLSKey,
  (newVal: string) => {
    loadColumnsAndSearch(newVal);
  },
  { immediate: true },
);

watch(
  selectedDomain,
  (newVal) => {
    if (newVal != null) {
      checkUnscheduledParcelShipmentsExist();
    }
  },
  { immediate: true },
);

watch(
  isLocationGroupingEnabled,
  (enabled) => {
    if (enabled) {
      loadLocationGroups({ includeVisible: true });
    }
  },
  { immediate: true },
);
</script>

<style lang="scss">
@import '~@rygen/rygen-ui/assets/styles/animation/flash.css';
.shipment-list {
  .search-result-table-and-filter-wrapper {
    &.new-search-toggle {
      --app-chrome-main-content-header-height: 5.2rem;
    }
  }
}
</style>
